import apiClient from "./apiClient";
import { buildListParams } from "../lib/buildListParams";
import { downloadBlob } from "../lib/downloadFile";

export interface InventoryCategory {
    id: number;
    name: string;
    description: string;
    /** Inactive categories stay on existing items but leave new-selection lists. */
    isActive: boolean;
    items?: InventoryItem[];
}

export interface InventoryItem {
    id: number;
    name: string;
    categoryId: number;
    category?: InventoryCategory;
    isRefundable: boolean;
    totalStock: number;
    availableStock: number;
    // Units written off as lost. Usable stock = totalStock - lostStock. (Story 57)
    lostStock?: number;
    minStockLevel: number;
    unitsPerPack: number;
    status: string;
    assignments?: InventoryAssignment[];
    purchases?: InventoryPurchase[];
    createdAt?: string;
    updatedAt?: string;
}

export interface InventoryPurchase {
    id: number;
    vendorName: string;
    invoiceNumber: string;
    purchaseDate: string;
    itemId: number;
    item?: InventoryItem;
    quantity: number;
    packQuantity?: number | null;
    unitsPerPack?: number;
    unitCost: number;
    totalCost: number;
    remarks: string;
    currency?: string;
    invoiceAttachment?: string;
}

export interface InventoryAssignment {
    id: number;
    targetType: 'PERSON' | 'LOCATION';
    userId?: number;
    user?: { id: number; firstName: string; lastName: string; email: string };
    location?: string;
    department?: string;
    itemId: number;
    item?: InventoryItem;
    quantity: number;
    assignmentDate: string;
    expectedReturnDate?: string;
    // 'lost' = written off on return, never restocked (Story 57)
    status: 'assigned' | 'returned' | 'closed' | 'lost';
}

// Single-assignment payload from GET /assignments/:id — the list shape plus
// the separately-stored return record (null until the item comes back).
export interface InventoryAssignmentDetail extends InventoryAssignment {
    createdAt?: string;
    updatedAt?: string;
    user?: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
        designation?: string;
        department?: { id: number; name: string };
    };
    returnRecord?: {
        id: number;
        returnDate: string;
        condition?: string;
        remarks?: string;
        approvedBy?: { id: number; firstName: string; lastName: string; email: string };
    } | null;
}

export interface InventoryTransaction {
    id: number;
    itemId: number;
    item?: InventoryItem;
    type: 'IN' | 'OUT' | 'RETURN' | 'ADJUSTMENT';
    quantity: number;
    referenceId?: number;
    referenceType?: string;
    transactionDate: string;
    performedBy?: any;
    notes?: string;
}

export interface DashboardStats {
    totalItems: number;
    totalStock: number;
    lowStockAlert: number;
    assignedItems: number;
    pendingReturns: number;
    refundableItemsCount: number;
}

export interface InventoryItemListResult {
    data: InventoryItem[];
    total: number;
}

// Filters accepted by GET /api/inventory-management/items — mirrors backend
// InventoryItemQueryDto. Array fields are sent comma-joined.
export interface InventoryItemListFilters {
    search?: string;
    tab?: 'all' | 'in-stock' | 'low-stock' | 'out-of-stock';
    categoryId?: number[];
    vendor?: string[];
    isRefundable?: 'true' | 'false';
    status?: 'active' | 'inactive';
    minStockLevelMin?: number;
    minStockLevelMax?: number;
    totalStockMin?: number;
    totalStockMax?: number;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    page?: number;
    limit?: number;
}


export const inventoryService = {
    // Categories
    getCategories: async () => {
        const response = await apiClient.get<InventoryCategory[]>("/api/inventory-management/categories");
        return response.data;
    },
    createCategory: async (data: any) => {
        const response = await apiClient.post<InventoryCategory>("/api/inventory-management/categories", data);
        return response.data;
    },
    getCategory: async (id: number) => {
        const response = await apiClient.get<InventoryCategory>(`/api/inventory-management/categories/${id}`);
        return response.data;
    },
    updateCategory: async (id: number, data: any) => {
        const response = await apiClient.put<InventoryCategory>(`/api/inventory-management/categories/${id}`, data);
        return response.data;
    },
    deleteCategory: async (id: number) => {
        const response = await apiClient.delete(`/api/inventory-management/categories/${id}`);
        return response.data;
    },

    // Items
    getItems: async (filters: InventoryItemListFilters = {}, signal?: AbortSignal): Promise<InventoryItemListResult> => {
        const params = buildListParams(filters);
        const response = await apiClient.get<InventoryItemListResult | InventoryItem[]>("/api/inventory-management/items", { params, signal });
        // Defensive: normalize a bare array (e.g. a stale/un-rebuilt backend
        // still on the pre-pagination response shape) instead of crashing.
        if (Array.isArray(response.data)) {
            return { data: response.data, total: response.data.length };
        }
        return {
            data: response.data?.data ?? [],
            total: response.data?.total ?? 0,
        };
    },
    getItem: async (id: number) => {
        const response = await apiClient.get<InventoryItem>(`/api/inventory-management/items/${id}`);
        return response.data;
    },
    createItem: async (data: any) => {
        const response = await apiClient.post<InventoryItem>("/api/inventory-management/items", data);
        return response.data;
    },
    updateItem: async (id: number, data: any) => {
        const response = await apiClient.put<InventoryItem>(`/api/inventory-management/items/${id}`, data);
        return response.data;
    },
    deleteItem: async (id: number) => {
        const response = await apiClient.delete(`/api/inventory-management/items/${id}`);
        return response.data;
    },
    adjustStock: async (data: { itemId: number; quantity: number; type: 'IN' | 'OUT'; notes: string }) => {
        const response = await apiClient.post<InventoryTransaction>("/api/inventory-management/items/adjust-stock", data);
        return response.data;
    },
    downloadItemImportTemplate: async (): Promise<void> => {
        const response = await apiClient.get('/api/inventory-management/items/import/template', {
            responseType: 'blob',
        });
        downloadBlob(response.data, 'inventory_items_import_template.xlsx');
    },
    validateItemImport: async (file: File): Promise<{ data: any[] }> => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await apiClient.post<{ data: any[] }>('/api/inventory-management/items/import/validate', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },
    confirmItemImport: async (items: any[]): Promise<{ success: number; failed: number; errors: any[] }> => {
        const response = await apiClient.post<{ success: number; failed: number; errors: any[] }>('/api/inventory-management/items/import/confirm', { items });
        return response.data;
    },

    // Purchases
    getPurchases: async () => {
        const response = await apiClient.get<InventoryPurchase[]>("/api/inventory-management/purchases");
        return response.data;
    },
    createPurchase: async (data: any) => {
        const response = await apiClient.post<InventoryPurchase>("/api/inventory-management/purchases", data);
        return response.data;
    },

    // Assignments
    getAssignments: async (userId?: number) => {
        const response = await apiClient.get<InventoryAssignment[]>("/api/inventory-management/assignments", {
            params: { userId },
        });
        return response.data;
    },
    getAssignment: async (id: number) => {
        const response = await apiClient.get<InventoryAssignmentDetail>(`/api/inventory-management/assignments/${id}`);
        return response.data;
    },
    updateExpectedReturn: async (id: number, expectedReturnDate: string, reason?: string) => {
        const response = await apiClient.put<InventoryAssignmentDetail>(
            `/api/inventory-management/assignments/${id}/expected-return`,
            { expectedReturnDate, reason },
        );
        return response.data;
    },
    createAssignment: async (data: any) => {
        const response = await apiClient.post<InventoryAssignment>("/api/inventory-management/assignments", data);
        return response.data;
    },

    returnItem: async (assignmentId: number, data: { condition?: string; remarks?: string }) => {
        const response = await apiClient.post(`/api/inventory-management/assignments/${assignmentId}/return`, data);
        return response.data;
    },

    deleteMistakenAssignment: async (assignmentId: number, reason: string) => {
        const response = await apiClient.post(`/api/inventory-management/assignments/${assignmentId}/delete-mistake`, { reason });
        return response.data;
    },

    // Returns
    createReturn: async (data: any) => {
        const response = await apiClient.post("/api/inventory-management/returns", data);
        return response.data;
    },

    // Transactions
    getTransactions: async (itemId?: number) => {
        const response = await apiClient.get<InventoryTransaction[]>("/api/inventory-management/transactions", {
            params: { itemId },
        });
        return response.data;
    },

    // Dashboard
    getDashboardStats: async () => {
        const response = await apiClient.get<DashboardStats>("/api/inventory-management/dashboard");
        return response.data;
    },
};
